import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
    EQUIPMENT_OPTIONAL_CATEGORIES,
    CategoryDefaultIcon,
    getOptionalEquipmentCategories,
} from "../../StudioErstellen/EquipmentOptionalCategories.jsx";
import { SmallModal } from "../../UI/SmallModal/SmallModal";
import ErrorMessage from "../../StudioErstellen/ErrorMessage.jsx";
import { resolvePaidEquipmentImageSrc } from "../../../pages/StudioProfile/paidEquipmentHelpers";
import { isCategoryDefaultImageKey } from "../../../util/paidEquipmentCategoryDefaultImage.jsx";
import { useStudioEditor } from "../../../store/studioEditorStore";
import classes from "../../StudioErstellen/EquipmentOptional.module.css";

const ACCEPT_IMAGE = ".jpg,.jpeg,.png,image/jpeg,image/png";
const ALLOWED_IMAGE_TYPES = new Set(["image/jpeg", "image/jpg", "image/pjpeg", "image/png", "image/x-png"]);
const ALLOWED_IMAGE_EXT = /\.(jpe?g|png)$/i;
const NAME_MAX = 50;
const DESC_MAX = 140;

const isAllowedImageFile = (file) => {
    if (!file) return false;
    if (file.type && ALLOWED_IMAGE_TYPES.has(file.type)) return true;
    if (file.name && ALLOWED_IMAGE_EXT.test(file.name)) return true;
    return false;
};

function newId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function generateImageId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

const convertFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

async function imageFromFile(file) {
    const id = generateImageId();
    const previewUrl = await convertFileToBase64(file);
    return {
        id,
        file,
        fileName: `${id}-${file.name}`,
        type: file.type,
        previewUrl,
    };
}

function parsePrice(value) {
    const normalized = String(value || "").trim().replace(/\s/g, "").replace(",", ".");
    if (normalized === "" || normalized === ".") return null;
    const n = parseFloat(normalized);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
}

function sanitizePriceInput(raw) {
    if (raw == null) return "";
    let t = String(raw).replace(/[^\d.,]/g, "");
    if (!t) return "";
    const sepIndex = t.search(/[.,]/);
    if (sepIndex === -1) return t.replace(/[^\d]/g, "");
    const intPart = t.slice(0, sepIndex).replace(/[^\d]/g, "");
    const decPart = t.slice(sepIndex + 1).replace(/[^\d]/g, "");
    const sep = t[sepIndex];
    if (intPart === "" && decPart) return `0${sep}${decPart}`;
    return intPart + sep + decPart;
}

const PlusGlyph = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden>
        <path fillRule="evenodd" clipRule="evenodd" d="M16.0469 8.58248H0.046875V7.50617H16.0469V8.58248Z" fill="#1F1F1F" />
        <path fillRule="evenodd" clipRule="evenodd" d="M8.58121 0.0507812V16.0508H7.50489V0.0507812H8.58121Z" fill="#1F1F1F" />
        <path fillRule="evenodd" clipRule="evenodd" d="M16.0469 8.58248H0.046875V7.50617H16.0469V8.58248Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10" />
        <path fillRule="evenodd" clipRule="evenodd" d="M8.58121 0.0507812V16.0508H7.50489V0.0507812H8.58121Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10" />
    </svg>
);

const ChevronDown = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden>
        <path d="M1 1.5L6 6.5L11 1.5" stroke="#1F1F1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.2689 8.72266L0.201172 1.65492L1.13344 0.722656L8.20117 7.79039L15.2689 0.722656L16.2012 1.65492L9.13344 8.72266L16.2012 15.7904L15.2689 16.7227L8.20117 9.65493L1.13344 16.7227L0.201172 15.7904L7.2689 8.72266Z"
            fill="#696F76"
        />
    </svg>
);

const normalizeItemsForCompare = (list = []) =>
    list
        .map((it, index) => ({
            id: it?.id || "",
            category: it?.category || "",
            nameModel: it?.nameModel || "",
            quantity: Math.max(1, Number(it?.quantity || 1)),
            pricePerPiece: Number(it?.pricePerPiece || 0),
            description: (it?.description || "").slice(0, DESC_MAX),
            imageKey: it?.imageKey || null,
            defaultThumbnailFromCategory: Boolean(it?.defaultThumbnailFromCategory),
            sortOrder: typeof it?.sortOrder === "number" ? it.sortOrder : index,
        }))
        .sort((a, b) => a.sortOrder - b.sortOrder);

export const EquipmentOptionalEditor = ({ studio, validate, onDirtyChange }) => {
    const { updateField } = useStudioEditor();
    const [error, setError] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [items, setItems] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [category, setCategory] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [nameModel, setNameModel] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [priceRaw, setPriceRaw] = useState("");
    const [description, setDescription] = useState("");
    const [draftImage, setDraftImage] = useState(null);
    const [existingImageKey, setExistingImageKey] = useState(null);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [editBaseline, setEditBaseline] = useState(null);
    const fileInputRef = useRef(null);
    const dropdownRef = useRef(null);
    const pickingFileRef = useRef(false);
    const modalBodyRef = useRef(null);

    const initialItemsRef = useRef("[]");
    const lastSerializedRef = useRef("[]");
    const lastDirtyRef = useRef(false);
    const onDirtyChangeRef = useRef(onDirtyChange);
    const updateFieldRef = useRef(updateField);

    useEffect(() => {
        onDirtyChangeRef.current = onDirtyChange;
    }, [onDirtyChange]);

    useEffect(() => {
        updateFieldRef.current = updateField;
    }, [updateField]);

    useEffect(() => {
        const mapped = Array.isArray(studio?.optionalBookableEquipment)
            ? studio.optionalBookableEquipment.map((it, index) => ({
                  id: it.id || newId(),
                  category: it.category || "",
                  nameModel: it.nameModel || "",
                  quantity: Math.max(1, Number(it.quantity || 1)),
                  pricePerPiece: Number(it.pricePerPiece || 0),
                  description: it.description || "",
                  imageKey: it.imageKey || null,
                  defaultThumbnailFromCategory:
                      !it.imageKey || isCategoryDefaultImageKey(it.imageKey),
                  sortOrder: typeof it.sortOrder === "number" ? it.sortOrder : index,
                  image: null,
                  imageUrl: null,
              }))
            : [];
        setItems(mapped);
        const normalized = normalizeItemsForCompare(mapped);
        const serialized = JSON.stringify(normalized);
        initialItemsRef.current = serialized;
        lastSerializedRef.current = serialized;
        lastDirtyRef.current = false;
        updateFieldRef.current?.("optionalBookableEquipment", undefined);
        onDirtyChangeRef.current?.(false);
    }, [studio?.id, studio?.optionalBookableEquipment]);

    useEffect(() => {
        const normalized = normalizeItemsForCompare(items);
        const serialized = JSON.stringify(normalized);
        if (serialized === lastSerializedRef.current) return;
        lastSerializedRef.current = serialized;
        const dirty = serialized !== initialItemsRef.current;
        if (dirty !== lastDirtyRef.current) {
            onDirtyChangeRef.current?.(dirty);
            lastDirtyRef.current = dirty;
        }
        updateFieldRef.current?.("optionalBookableEquipment", dirty ? items : undefined);
    }, [items]);

    const runFilePicker = useCallback(() => {
        pickingFileRef.current = true;
        const endPick = () => {
            pickingFileRef.current = false;
            window.removeEventListener("focus", endPick);
        };
        window.addEventListener("focus", endPick, { once: true });
        window.setTimeout(endPick, 1200);
        requestAnimationFrame(() => fileInputRef.current?.click());
    }, []);

    const resetForm = useCallback(() => {
        setEditingId(null);
        setCategory("");
        setDropdownOpen(false);
        setNameModel("");
        setQuantity(1);
        setPriceRaw("");
        setDescription("");
        setDraftImage(null);
        setExistingImageKey(null);
        setSubmitAttempted(false);
        setEditBaseline(null);
    }, []);

    const availableCategories = useMemo(
        () => getOptionalEquipmentCategories(studio?.studioTypes),
        [studio?.studioTypes]
    );

    const dropdownCategories = useMemo(() => {
        if (category && !availableCategories.includes(category)) {
            return [category, ...availableCategories];
        }
        return availableCategories;
    }, [availableCategories, category]);

    const openModalNew = useCallback((preselectCategory = "") => {
        resetForm();
        if (preselectCategory && availableCategories.includes(preselectCategory)) {
            setCategory(preselectCategory);
        }
        setModalOpen(true);
    }, [resetForm, availableCategories]);

    const openModalEdit = useCallback((item) => {
        const localDraftImage =
            item?.image && item.image.previewUrl
                ? item.image
                : item?.imageUrl
                ? { id: `preview-${item.id || "local"}`, previewUrl: item.imageUrl, file: null }
                : null;
        setEditBaseline({
            category: item.category || "",
            nameModel: item.nameModel || "",
            quantity: Math.max(1, item.quantity || 1),
            priceRaw: item.pricePerPiece != null ? String(item.pricePerPiece).replace(".", ",") : "",
            description: (item.description || "").slice(0, DESC_MAX),
            draftImageId: localDraftImage?.id ?? null,
            existingImageKey: item.imageKey || null,
        });
        setEditingId(item.id);
        setCategory(item.category || "");
        setNameModel(item.nameModel || "");
        setQuantity(Math.max(1, item.quantity || 1));
        setPriceRaw(item.pricePerPiece != null ? String(item.pricePerPiece).replace(".", ",") : "");
        setDescription(item.description || "");
        setDraftImage(localDraftImage);
        setExistingImageKey(item.imageKey || null);
        setDropdownOpen(false);
        setSubmitAttempted(false);
        setModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleBackdropClose = useCallback(() => {
        if (pickingFileRef.current) return;
        setModalOpen(false);
        resetForm();
    }, [resetForm]);

    useEffect(() => {
        if (!dropdownOpen) return;
        const onDoc = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [dropdownOpen]);

    const handleImagePick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || !isAllowedImageFile(file)) return;
        try {
            const img = await imageFromFile(file);
            setDraftImage(img);
        } catch (_) {
            // ignore
        }
    };

    useEffect(() => {
        if (validate) {
            validate.current = () => true;
        }
    }, [validate]);

    const grouped = useMemo(() => {
        const map = {};
        for (const cat of availableCategories) map[cat] = [];
        for (const it of items) {
            const c = it.category;
            if (!map[c]) map[c] = [];
            map[c].push(it);
        }
        return map;
    }, [items, availableCategories]);

    const visibleCategories = useMemo(() => {
        const withItems = Object.keys(grouped).filter((c) => grouped[c].length > 0);
        return EQUIPMENT_OPTIONAL_CATEGORIES.filter((c) => withItems.includes(c));
    }, [grouped]);
    const existingImagePreviewSrc = useMemo(
        () => (existingImageKey ? resolvePaidEquipmentImageSrc({ imageKey: existingImageKey }) : ""),
        [existingImageKey]
    );
    const modalTitleText = category ? `${category} hinzufügen` : "Lichtequipment hinzufügen";
    const modalHeaderTitle = editingId ? `${category || "Equipment"} bearbeiten` : modalTitleText;
    const nameOver = nameModel.length > NAME_MAX;
    const priceOk = parsePrice(priceRaw) != null;
    const canSubmit = Boolean(category && nameModel.trim() && priceOk);
    const showFormErrors = submitAttempted && !canSubmit;

    const isEditUnchanged = useMemo(() => {
        if (!editingId || !editBaseline) return false;
        return (
            category === editBaseline.category &&
            nameModel === editBaseline.nameModel &&
            quantity === editBaseline.quantity &&
            priceRaw === editBaseline.priceRaw &&
            description.slice(0, DESC_MAX) === editBaseline.description &&
            (draftImage?.id ?? null) === editBaseline.draftImageId &&
            existingImageKey === editBaseline.existingImageKey
        );
    }, [editingId, editBaseline, category, nameModel, quantity, priceRaw, description, draftImage, existingImageKey]);

    const handleSubmit = async () => {
        if (!canSubmit) {
            setSubmitAttempted(true);
            return;
        }
        if (editingId && isEditUnchanged) return;

        try {
            setIsSaving(true);
            setError("");
            const price = parsePrice(priceRaw);
            const itemPatch = {
                category,
                nameModel: nameModel.trim(),
                quantity,
                pricePerPiece: price,
                description: description.slice(0, DESC_MAX),
                defaultThumbnailFromCategory: !draftImage && !existingImageKey,
                imageKey: existingImageKey || null,
                image: draftImage || null,
                imageUrl: draftImage?.previewUrl || null,
            };

            if (editingId) {
                setItems((prev) =>
                    prev.map((it) =>
                        it.id === editingId
                            ? {
                                  ...it,
                                  ...itemPatch,
                              }
                            : it
                    )
                );
            } else {
                setItems((prev) => [
                    ...prev,
                    {
                        id: newId(),
                        sortOrder: prev.length,
                        ...itemPatch,
                    },
                ]);
            }
            closeModal();
        } catch (e) {
            setError(e?.message || "Fehler beim Speichern des Equipments.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            setError("");
            setItems((prev) => prev.filter((it) => it.id !== id));
        } catch (e) {
            setError(e?.message || "Fehler beim Löschen des Equipments.");
        }
    };

    const handleDeleteEditing = async () => {
        if (!editingId) return;
        await handleDelete(editingId);
        closeModal();
    };

    useEffect(() => {
        if (!modalOpen) return;
        requestAnimationFrame(() => {
            if (modalBodyRef.current) {
                modalBodyRef.current.scrollTop = 0;
            }
        });
    }, [modalOpen, editingId]);

    return (
        <div className={classes.sectionEdit}>
            <h2 className={`${classes.titleMain} titleSemibold`}>
                Kostenpflichtiges Equipment{" "}
            </h2>

            {visibleCategories.map((cat) => (
                <div key={cat} className={classes.categoryBlock}>
                    <div className={classes.categoryHeaderRow}>
                        <h3 className={classes.categoryName}>{cat}</h3>
                        <button type="button" className={classes.categoryAddBtn} onClick={() => openModalNew(cat)} aria-label={`Equipment unter ${cat} hinzufügen`}>
                            <PlusGlyph />
                        </button>
                    </div>
                    {grouped[cat].map((it) => (
                        <div key={it.id || `${cat}-${it.nameModel}-${it.sortOrder}`} className={classes.equipmentCard}>
                            <div className={classes.cardThumb}>
                                {resolvePaidEquipmentImageSrc(it) ? (
                                    <img src={resolvePaidEquipmentImageSrc(it)} alt="" />
                                ) : (
                                    <div className={classes.cardThumbSvg}>
                                        <CategoryDefaultIcon category={it.category} />
                                    </div>
                                )}
                            </div>
                            <div className={classes.cardBody}>
                                <div className={classes.cardTitleRow}>
                                    <span className={`${classes.cardTitle} semiboldMiddle`} title={it.nameModel}>
                                        {it.nameModel}
                                    </span>
                                    {it.quantity > 1 && (
                                        <span className={`${classes.qtyBadge} semiboldXxs`}>{it.quantity} Stück</span>
                                    )}
                                </div>
                                <span className="regularSmall" style={{ color: "var(--dark-grey-4)" }}>
                                    {Number(it.pricePerPiece).toFixed(2)} € / Stück
                                </span>
                            </div>
                            <div className={classes.cardActionsEdit}>
                                <button type="button" className={classes.iconBtn} onClick={() => openModalEdit(it)} aria-label="Bearbeiten">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M9.76771 0.217129C9.98643 -0.00159099 10.318 -0.0615026 10.5994 0.0668361C11.2073 0.344035 11.8619 0.830944 12.4315 1.36092C13.0042 1.89375 13.5426 2.51823 13.8952 3.10918C14.0692 3.4007 14.0229 3.77315 13.7829 4.0132L11.9929 5.80226C11.9725 5.82356 11.9518 5.84423 11.9308 5.8643L10.424 7.37041C10.0956 7.6989 9.76709 8.02749 9.43851 8.35615C7.72973 10.0653 6.01946 11.776 4.31334 13.4809C4.18551 13.6087 4.0145 13.6862 3.83412 13.6973L0.786912 13.8854C0.57476 13.8985 0.367195 13.8199 0.216943 13.6695C0.0666913 13.5192 -0.0117888 13.3116 0.00143842 13.0994L0.188077 10.106C0.186336 9.91408 0.258695 9.72158 0.405154 9.57512L1.60469 8.37559C1.62213 8.35814 1.64023 8.34175 1.65891 8.3264L7.97034 2.01497C7.97518 2.00983 7.98009 2.00476 7.98508 1.99976C8.47303 1.51054 8.92015 1.06391 9.30213 0.682353C9.47071 0.51396 9.6266 0.358241 9.76771 0.217129ZM2.59409 9.48275L1.65369 10.4232L1.5333 12.354L3.46273 12.2349L8.98221 6.71547C8.98206 6.71561 8.98235 6.71533 8.98221 6.71547L9.51111 6.18657C9.13378 6.02376 8.78631 5.78614 8.49425 5.51573C8.16366 5.20965 7.8695 4.82958 7.67534 4.40781L7.06699 5.01672C4.94743 7.13446 4.77549 7.3064 2.65774 9.42597C2.63746 9.44627 2.6162 9.4652 2.59409 9.48275ZM9.04288 3.03893C8.92298 3.17516 8.88427 3.33878 8.94478 3.57424C9.01337 3.84116 9.20793 4.15615 9.50143 4.42789C9.79494 4.69963 10.1239 4.8693 10.3951 4.91707C10.6149 4.95576 10.768 4.91575 10.8898 4.80786C10.9101 4.78753 10.9305 4.7672 10.9508 4.74688C10.9514 4.74627 10.952 4.74565 10.9526 4.74504C10.9601 4.73757 10.9677 4.73029 10.9754 4.72321L12.2993 3.40002C12.0566 3.0855 11.7536 2.75515 11.4217 2.44629C11.0828 2.13101 10.7369 1.85963 10.4224 1.65869C10.3981 1.683 10.3735 1.70759 10.3486 1.73244C9.97181 2.10884 9.53292 2.54725 9.05309 3.02828C9.04973 3.03186 9.04633 3.03541 9.04288 3.03893Z" fill="black" />
                                    </svg>
                                </button>
                                <button type="button" className={`${classes.iconBtn} ${classes.iconBtnDeleteEdit}`} onClick={() => handleDelete(it.id)} aria-label="Löschen">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                      <path d="M8.89551 0.0996094C9.58831 0.0996336 10.1491 0.659875 10.1494 1.35254V2.16797H13.7412C14.4339 2.16797 14.9958 2.72815 14.9961 3.4209V4.5752C14.996 5.09574 14.677 5.53595 14.2266 5.72559L13.5586 14.1553L13.541 14.3711L13.5244 14.3545C13.391 14.9121 12.8866 15.2988 12.3145 15.2988H2.80176C2.14224 15.2988 1.59939 14.7982 1.54102 14.1436L1.54004 14.1348V14.1309L0.869141 5.71582C0.420447 5.5255 0.103756 5.08649 0.103516 4.56738V3.4209C0.103828 2.72839 0.663915 2.16826 1.35645 2.16797H4.94141V1.35254C4.9417 0.659941 5.50168 0.0997764 6.19434 0.0996094H8.89551ZM2.59277 14.0557C2.60531 14.1669 2.69378 14.2441 2.80176 14.2441H12.3145C12.4251 14.2441 12.4998 14.1656 12.5059 14.0781L12.5068 14.0684L13.1611 5.82031H1.9375L2.59277 14.0557ZM7.54492 6.9707C7.83603 6.9707 8.07203 7.207 8.07227 7.49805V12.123C8.07193 12.414 7.83602 12.6504 7.54492 12.6504C7.25394 12.6503 7.01791 12.4139 7.01758 12.123V7.49805C7.01781 7.20701 7.25393 6.97079 7.54492 6.9707ZM4.72852 6.98145C4.9506 6.96146 5.15262 7.08258 5.24707 7.27051L5.28125 7.35449L5.28418 7.36523H5.2832L5.2998 7.4502L5.30078 7.45898L5.71973 12.0664L5.7207 12.0762L5.62012 12.0752L5.71973 12.0762V12.1729L5.61914 12.1621L5.71875 12.1729C5.69151 12.4165 5.49654 12.6152 5.24316 12.6387C4.9896 12.6617 4.76036 12.5016 4.68945 12.2666L4.68652 12.2568V12.2559L4.6709 12.1719L4.66895 12.1621L4.25098 7.55469L4.25 7.54492H4.25098L4.25195 7.45801V7.44824L4.26953 7.35938C4.32835 7.15743 4.506 7.00168 4.72852 6.98145ZM10.3711 6.98145L10.4756 7.00195C10.7104 7.07282 10.8705 7.30123 10.8477 7.55469L10.4287 12.1621L10.4277 12.1709L10.4121 12.2559L10.4102 12.2666H10.4092C10.3383 12.5017 10.11 12.6617 9.85645 12.6387C9.56652 12.6123 9.35282 12.3562 9.37891 12.0664L9.79785 7.45898L9.79883 7.44922L9.81543 7.36426L9.81738 7.35449H9.81836C9.88913 7.11963 10.1172 6.95837 10.3711 6.98145ZM1.35645 3.22266C1.24646 3.22294 1.15851 3.31086 1.1582 3.4209V4.56738C1.15847 4.6595 1.22054 4.73528 1.30566 4.75781H13.8145C13.889 4.7285 13.9414 4.65864 13.9414 4.5752V3.4209C13.9411 3.3109 13.8517 3.22266 13.7412 3.22266H1.35645ZM6.19434 1.1543C6.08425 1.15446 5.99638 1.24237 5.99609 1.35254V2.16797H9.09473V1.35254C9.09444 1.2422 9.00563 1.15432 8.89551 1.1543H6.19434Z" fill="#CE425D" stroke="#CE425D" strokeWidth="0.2" strokeMiterlimit="10"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ))}

            <button type="button" className={`${classes.addTrigger} ${items.length > 0 ? classes.addTriggerCompact : ""}`} onClick={() => openModalNew()}>
                <div className={classes.addTriggerContent}>
                    <span className="slimRegularLargeText">Equipment hinzufügen</span>
                    <span className={classes.addTriggerIcon}><PlusGlyph /></span>
                </div>
            </button>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <SmallModal
                open={modalOpen}
                onBackdropClick={handleBackdropClose}
                className={classes.modalDialogOverride}
                fromCreateEditService={true}
                equipmentOptionalSheet={true}
            >
                <div className={classes.modalShell} onClick={(e) => e.stopPropagation()}>
                    <header className={classes.modalHeader}>
                        <div className={classes.modalHeaderInner}>
                            <h2 className={`${classes.modalTitle} titleSemibold3`} title={modalHeaderTitle}>{modalHeaderTitle}</h2>
                            <button type="button" className={classes.modalClose} onClick={closeModal} aria-label="Schließen">
                                <CloseIcon />
                            </button>
                        </div>
                    </header>

                    {editingId && (
                        <div className={classes.modalDeleteRow}>
                            <button
                                type="button"
                                className={`${classes.modalDeleteEquipmentBtn} semiboldMiddle`}
                                onClick={handleDeleteEditing}
                                disabled={isSaving}
                            >
                                Equipment entfernen
                            </button>
                        </div>
                    )}

                    <div ref={modalBodyRef} className={classes.modalBody}>
                        <input ref={fileInputRef} type="file" accept={ACCEPT_IMAGE} className={classes.hiddenFileInput} onChange={handleImagePick} />
                        <div className={classes.containerField}>
                            <span className={`${classes.fieldLabel} semiboldMiddle`}>Foto</span>
                            <div className={classes.imageRow}>
                                <div role="button" tabIndex={0} className={`${classes.imageDropZone} ${draftImage ? classes.imageDropZoneFilled : ""}`} onClick={() => !draftImage && runFilePicker()}>
                                    {draftImage ? (
                                        <img src={draftImage.previewUrl} alt="" />
                                    ) : existingImagePreviewSrc ? (
                                        <img src={existingImagePreviewSrc} alt="" />
                                    ) : (
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                            <p className={`${classes.dropHint} regularMiddle`}>Optional Foto hinzufügen</p>
                                        </div>
                                    )}
                                </div>
                                {(draftImage || existingImageKey) && (
                                    <div className={classes.imageActions}>
                                        <button type="button" className={`${classes.replacePhotoBtn} semiboldSmall`} onClick={runFilePicker}>Foto ersetzen</button>
                                        <button type="button" className={`${classes.removePhotoBtn} semiboldSmall`} onClick={() => { setDraftImage(null); setExistingImageKey(null); }}>Foto löschen</button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={classes.containerField}>
                            <span className={`${classes.fieldLabel} semiboldMiddle`}>Equipment-Kategorie</span>
                            <div className={classes.dropdownWrap} ref={dropdownRef}>
                                <button type="button" className={classes.dropdownBtn} onClick={() => setDropdownOpen((o) => !o)} aria-expanded={dropdownOpen}>
                                    <span className={category ? undefined : classes.dropdownPlaceholder}>{category || "z.B. Lichtequipment"}</span>
                                    <ChevronDown />
                                </button>
                                {dropdownOpen && (
                                    <div className={classes.dropdownMenu} role="listbox">
                                        {dropdownCategories.map((c) => (
                                            <button key={c} type="button" className={`${classes.dropdownItem} regularMiddle`} onClick={() => { setCategory(c); setDropdownOpen(false); }}>
                                                {c}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={classes.containerField}>
                            <span className={`${classes.fieldLabel} semiboldMiddle`}>Name/Modell</span>
                            <div className={classes.namePriceRow}>
                                <input className={`${classes.nameInput} regularMiddle`} placeholder="Name/Modell eingeben" value={nameModel} onChange={(e) => setNameModel(e.target.value)} />
                                <div className={classes.stepper}>
                                    <button type="button" className={classes.stepperBtn} onClick={() => setQuantity((q) => Math.max(1, q - 1))} aria-label="Weniger">-</button>
                                    <span className={`${classes.stepperVal} regularLarge`}>{quantity}</span>
                                    <button type="button" className={classes.stepperBtn} onClick={() => setQuantity((q) => q + 1)} aria-label="Mehr">+</button>
                                </div>
                            </div>
                            <div className={`slimRegularSmallText ${classes.charCounter} ${nameOver ? classes.charCounterWarn : ""}`}>{nameModel.length}/{NAME_MAX}</div>
                        </div>

                        <div className={classes.containerField}>
                            <span className={`${classes.fieldLabel} semiboldMiddle`}>Preis pro Stück</span>
                            <div className={classes.priceRow}>
                                <input className={`${classes.priceInput} regularMiddle`} placeholder="Preis" inputMode="decimal" autoComplete="off" value={priceRaw} onChange={(e) => setPriceRaw(sanitizePriceInput(e.target.value))} />
                                <span className={`${classes.priceSuffix} slimRegularLargeText`}>€ / Stück</span>
                            </div>
                        </div>

                        <div className={classes.containerField}>
                            <span className={`${classes.fieldLabel} semiboldMiddle`}>Beschreibung <span className={classes.labelMuted}>(optional)</span></span>
                            <textarea className={`${classes.textarea} regularMiddle`} placeholder="Beschreibung (optional)" maxLength={DESC_MAX} value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
                            <div className={`slimRegularSmallText ${classes.charCounter}`}>{description.length}/{DESC_MAX}</div>
                        </div>
                    </div>

                    <footer className={classes.modalFooter}>
                        {showFormErrors && (
                            <div className={classes.modalFooterErrors}>
                                {!category && <ErrorMessage>Bitte eine Equipment-Kategorie wählen.</ErrorMessage>}
                                {!nameModel.trim() && <ErrorMessage>Bitte Name/Modell eingeben.</ErrorMessage>}
                                {(priceRaw.trim() === "" || parsePrice(priceRaw) == null) && <ErrorMessage>Bitte einen gültigen Preis größer als 0 eingeben.</ErrorMessage>}
                            </div>
                        )}
                        <button type="button" className={`${classes.submitBtn} mediumLarge ${!canSubmit || isSaving ? classes.submitBtnMuted : classes.submitBtnActive}`} onClick={handleSubmit} disabled={isSaving}>
                            {editingId ? "Speichern" : "Hinzufügen"}
                        </button>
                    </footer>
                </div>
            </SmallModal>
        </div>
    );
};

export default EquipmentOptionalEditor;

